import React, { useState, useMemo } from 'react';

const SVG_ICONS = {
  view: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  edit: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  duplicate: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  ),
  copy: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8z" />
      <polyline points="16 3 16 8 21 8" />
      <line x1="12" y1="11" x2="12" y2="17" />
      <line x1="9" y1="14" x2="15" y2="14" />
    </svg>
  ),
  delete: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  ),
  convert: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
  document: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  submit: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </svg>
  ),
  // 📋 "Line items / item details" — clipboard with check & rows
  lineitems: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M9 3h6v3H9z" />
      <path d="M8 11h8" />
      <path d="M8 15h5" />
    </svg>
  ),
  // 💰 "Cost sheet / pricing" — dollar/rupee coin
  costsheet: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M15 8h-4.5a2 2 0 0 0 0 4H13a2 2 0 0 1 0 4H8.5" />
      <path d="M12 6v12" />
    </svg>
  ),
};

export default function DataTable({
  columns = [],
  data = [],
  onRowClick,
  actions = [],
  pageSize = 15,
  searchable = true,
  loading = false,
  emptyMessage = 'No data found',
  emptyIcon = '\u{1F4CB}',
  fixedLayout = false
}) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);

  /* Filter data by search term */
  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const term = search.toLowerCase();
    return data.filter(row =>
      columns.some(col => {
        const val = row[col.key];
        return val != null && String(val).toLowerCase().includes(term);
      })
    );
  }, [data, search, columns]);

  /* Sort data */
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const col = columns.find(c => c.key === sortKey);
    return [...filtered].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      if (col && col.type === 'number') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (col && col.type === 'date') {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir, columns]);

  /* Paginate */
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function renderSortIcon(key) {
    if (sortKey !== key) return <span className="sort-icon">{'\u2195'}</span>;
    return <span className="sort-icon">{sortDir === 'asc' ? '\u2191' : '\u2193'}</span>;
  }

  function renderPageButtons() {
    const buttons = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button
          key={i}
          className={`page-btn${i === currentPage ? ' active' : ''}`}
          onClick={() => setPage(i)}
        >
          {i}
        </button>
      );
    }
    return buttons;
  }

  /* Reset to page 1 on search change */
  function handleSearch(e) {
    setSearch(e.target.value);
    setPage(1);
  }

  return (
    <div className="data-table-wrapper">
      {searchable && (
        <div className="table-toolbar">
          <div className="table-search">
            <span className="icon">{'\u{1F50D}'}</span>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={handleSearch}
            />
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 12, color: '#64748B' }}>
            {filtered.length} record{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {loading ? (
        <div className="table-loading">
          <div className="spinner" style={{ margin: '0 auto 12px' }}></div>
          <div>Loading data...</div>
        </div>
      ) : paginated.length === 0 ? (
        <div className="table-empty">
          <div className="icon">{emptyIcon}</div>
          <div>{emptyMessage}</div>
        </div>
      ) : (
        <div className="data-table-scroll">
        <table className={`data-table${fixedLayout ? ' data-table-fixed' : ''}`}>
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  onClick={() => col.sortable !== false && handleSort(col.key)}
                  style={{ cursor: col.sortable !== false ? 'pointer' : 'default', width: col.width, textAlign: (col.type === 'number' || col.align === 'right') ? 'right' : undefined }}
                >
                  {col.label}
                  {col.sortable !== false && renderSortIcon(col.key)}
                </th>
              ))}
              {actions.length > 0 && <th className="actions-cell actions-header" style={{ width: actions.length * 40 + 20, minWidth: actions.length * 40 + 20 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {paginated.map((row, idx) => (
              <tr
                key={row.id || idx}
                data-emp-id={row.id}
                onClick={() => onRowClick && onRowClick(row)}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {columns.map(col => {
                  const tdStyle = {};
                  if (col.type === 'number' || col.align === 'right') tdStyle.textAlign = 'right';
                  if (fixedLayout && !col.key.startsWith('_')) { tdStyle.overflow = 'hidden'; tdStyle.textOverflow = 'ellipsis'; tdStyle.whiteSpace = 'nowrap'; }
                  return (
                    <td key={col.key} style={tdStyle} title={fixedLayout && !col.render ? (row[col.key] ?? '') : undefined}>
                      {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                    </td>
                  );
                })}
                {actions.length > 0 && (
                  <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                    {actions.map((action, aIdx) => {
                      const lbl = action.label.toLowerCase().replace(/\s+/g, '');
                      const iconKey = action.icon || (
                        lbl.startsWith('copyto') ? 'copy' :
                        lbl.startsWith('view') ? 'view' :
                        lbl === 'details' ? 'view' :
                        lbl
                      );
                      const isDanger = iconKey === 'delete';
                      const extraClass = action.className || '';
                      // Support href actions that open URLs from row data
                      if (action.href) {
                        const url = typeof action.href === 'function' ? action.href(row) : row[action.href];
                        if (!url) return null;
                        return (
                          <a key={aIdx}
                            className={`action-btn action-btn--default ${extraClass}`}
                            href={url} target="_blank" rel="noopener noreferrer"
                            title={action.label} onClick={e => e.stopPropagation()}
                          >
                            {SVG_ICONS[iconKey] || SVG_ICONS.view}
                          </a>
                        );
                      }
                      return (
                        <button
                          key={aIdx}
                          className={`action-btn action-btn--${isDanger ? 'danger' : 'default'} ${extraClass}`}
                          onClick={() => action.onClick(row)}
                          title={action.label}
                        >
                          {SVG_ICONS[iconKey] || SVG_ICONS.view}
                        </button>
                      );
                    })}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {!loading && sorted.length > pageSize && (
        <div className="table-pagination">
          <span>
            Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, sorted.length)} of {sorted.length}
          </span>
          <div className="page-buttons">
            <button className="page-btn" onClick={() => setPage(1)} disabled={currentPage <= 1}>{'\u00AB'}</button>
            <button className="page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}>{'\u2039'}</button>
            {renderPageButtons()}
            <button className="page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>{'\u203A'}</button>
            <button className="page-btn" onClick={() => setPage(totalPages)} disabled={currentPage >= totalPages}>{'\u00BB'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
